import { Component, isDevMode, OnDestroy, OnInit } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CentreOption } from '../../../interfaces/Centres';
import { FullChild } from '../../../interfaces/FullChild';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { CentreService } from '../../../services/centre.service';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import {dE} from "@fullcalendar/core/internal-common";
import * as XLSX from "xlsx";
import {FinancialYearService} from "../../../services/financial-year.service";
import {endOfDay} from '../../../utils/date-range-format.service';

@Component({
  selector: 'app-education-follow-up-overview',
  templateUrl: './education-follow-up-overview.component.html',
  styleUrls: ['./education-follow-up-overview.component.scss']
})
export class EducationFollowUpOverviewComponent implements OnInit, OnDestroy {
  env = isDevMode() ? environment.testMode : environment.productionMode;
  private _destroyed$ = new Subject<void>();
  centres$: Observable<CentreOption[]>;
  children$: Observable<FullChild[]>;
  start: Date | any;
  end: Date | any;
  selectedChild: FullChild | undefined;
  selectedCentre: string = 'All';
  mData: any[] = [];
  searchText: string = '';

  private selectedCentreSubject = new BehaviorSubject<string>(this.selectedCentre);

  constructor(
    private readonly mCentreService: CentreService,
    private readonly mFirestore: AngularFirestore,
    private readonly financialYearService: FinancialYearService
  ) {
    this.centres$ = this.mCentreService.getAllCentresOptions().pipe(
      map(centres => [...centres].sort((a, b) => (a.centreName ?? '').trim().localeCompare((b.centreName ?? '').trim())))
    );

    // Observable to load children based on selected center
    this.children$ = this.selectedCentreSubject.asObservable().pipe(
      switchMap(centreCode => this.getAllotedChildListByCenter(centreCode))
    );
  }

  ngOnInit(): void {
    // Pre-fill the date range with the current financial year (search still requires an explicit click)
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.start = fy.startDate;
    this.end = fy.endDate;
    // Subscribe to selected centre and retrieve relevant children
    this.selectedCentreSubject.next(this.selectedCentre);
  }


  filterData(){

  }

  // Method to retrieve child data based on selected center
  private getAllotedChildListByCenter(centreCode: string): Observable<FullChild[]> {
    return this.mFirestore.collection(this.env.educationFollowUp, ref =>
      ref.where('centreCode', '==', centreCode)
    )
      .valueChanges()
      .pipe(
        map(data => {
          const uniqueChildren = new Map<string, any>();
          data.forEach((child: any) => {
            if (!uniqueChildren.has(child.childCode)) {
              uniqueChildren.set(child.childCode, {
                childCode: child.childCode,
                firstName: child.childName.split(' ')[0],
                lastName: child.childName.split(' ')[1] || ''
              });
            }
          });
          return Array.from(uniqueChildren.values()).sort((a, b) =>
            `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
          );
        })
      );
  }

  // Trigger center change and fetch children
  centreSelected(): void {
    console.log('Centre selected:', this.selectedCentre);
    this.selectedCentreSubject.next(this.selectedCentre);
  }
  OnChildSelected(){
    this.searchText = ''
  }

  search() {
    this.mData = [];
    let query = this.mFirestore.collection(this.env.educationFollowUp, ref => {
      let queryRef = ref.orderBy('date', 'desc');
      if (this.selectedCentre && this.selectedCentre !== 'All') {
        queryRef = queryRef.where('centreCode', '==', this.selectedCentre);
      }
      if (this.selectedChild) {
        queryRef = queryRef.where('childCode', '==', this.selectedChild.childCode);
      }
      if (this.start && this.end) {
        queryRef = queryRef.where('date', '>=', this.start).where('date', '<=', endOfDay(this.end));
      }
      return queryRef;
    }).valueChanges({ idField: 'id' });
    query.pipe(takeUntil(this._destroyed$)).subscribe(res => {
      this.mData = res;
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  // Clear search criteria
  mClear() {
    this.selectedCentre = '';
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.start = fy.startDate;
    this.end = fy.endDate;
    this.selectedChild = undefined;
    this.mData = [];
    this.searchText = ''
    this.selectedCentreSubject.next(this.selectedCentre); // Reset children list
  }

  downloadExcel(): void {
    if (!this.mData || this.mData.length === 0) {
      return;
    }

    // Take questions dynamically from first record
    const questions = this.mData[0]?.questions || [];

    const exportData = this.mData.map(row => {
      const obj: any = {
        Centre: row.centreName,
        'Child Name': row.childName,
        'Assessment Date': row.date?.toDate
          ? row.date.toDate().toLocaleDateString('en-GB')
          : row.date
      };

      // Add dynamic question columns
      questions.forEach((q: any, index: number) => {
        obj[q.question] = row.questions[index]?.ans || '';
      });

      return obj;
    });

    // Create worksheet & workbook
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
    const workbook: XLSX.WorkBook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Follow-Up Report');

    // Download file (NO file-saver)
    XLSX.writeFile(
      workbook,
      `Follow_Up_Report_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  }

}
